import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Award, Wrench, Download, ChevronRight, Loader2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Button } from '@/app/components/ui/button';
import { Badge } from '@/app/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/app/components/ui/tabs';
import { useComplianceData } from '@/hooks/use-compliance';
import { useOrgToday } from '@/hooks/use-settings';
import { formatDateOnly } from '@/lib/date-utils';
import type { ComplianceIssue } from '@/lib/types';

function isValidAssetId(assetId: number | null): assetId is number {
  return assetId !== null && Number.isInteger(assetId) && assetId > 0;
}

function getDaysOverdueBadge(days: number) {
  if (days > 90) {
    return <Badge variant="destructive">Critical: {days}d overdue</Badge>;
  } else if (days > 30) {
    return <Badge className="bg-orange-600">High: {days}d overdue</Badge>;
  } else {
    return <Badge className="bg-yellow-600">Medium: {days}d overdue</Badge>;
  }
}

function ComplianceIssueRow({
  issue,
  dateLabel,
}: {
  issue: ComplianceIssue;
  dateLabel: 'Expired' | 'Due';
}) {
  const content = (
    <div className="flex items-start justify-between gap-2">
      <div className="flex-1 min-w-0">
        <h4 className="font-medium text-sm truncate">{issue.asset_serial || 'Unknown asset'}</h4>
        <p className="text-xs text-gray-600 mt-1">{issue.issue_type}</p>
        <p className="text-xs text-gray-500 mt-0.5">
          {dateLabel}: {formatDateOnly(issue.due_date, 'N/A')}
        </p>
      </div>
      <div className="flex flex-col items-end gap-1">
        {getDaysOverdueBadge(issue.days_overdue)}
        {isValidAssetId(issue.asset_id) && <ChevronRight className="h-4 w-4 text-gray-400" />}
      </div>
    </div>
  );
  const className = 'block p-3 border rounded-lg';

  return isValidAssetId(issue.asset_id) ? (
    <Link
      to={`/assets/${issue.asset_id}`}
      className={`${className} hover:bg-gray-50 dark:hover:bg-accent transition-colors`}
    >
      {content}
    </Link>
  ) : (
    <div className={className}>{content}</div>
  );
}

export function CompliancePage() {
  const orgToday = useOrgToday();
  const { data: complianceData, isLoading, error } = useComplianceData();
  const [isExporting, setIsExporting] = useState(false);

  const expiredCertifications = complianceData?.expired_certifications || [];
  const overdueInspections = complianceData?.overdue_inspections || [];
  const overdueMaintenance = complianceData?.overdue_maintenance || [];

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const csvContent = [
        ['Compliance Report', `Generated on ${formatDateOnly(orgToday)}`],
        [],
        ['EXPIRED CERTIFICATIONS'],
        ['Asset Serial', 'Asset ID', 'Issue Type', 'Due Date', 'Days Overdue'],
        ...expiredCertifications.map(issue => [
          issue.asset_serial || 'N/A',
          issue.asset_id?.toString() || 'N/A',
          issue.issue_type,
          issue.due_date || 'N/A',
          issue.days_overdue.toString()
        ]),
        [],
        ['OVERDUE INSPECTIONS'],
        ['Asset Serial', 'Asset ID', 'Issue Type', 'Due Date', 'Days Overdue'],
        ...overdueInspections.map(issue => [
          issue.asset_serial || 'N/A',
          issue.asset_id?.toString() || 'N/A',
          issue.issue_type,
          issue.due_date || 'N/A',
          issue.days_overdue.toString()
        ]),
        [],
        ['OVERDUE MAINTENANCE'],
        ['Asset Serial', 'Asset ID', 'Issue Type', 'Due Date', 'Days Overdue'],
        ...overdueMaintenance.map(issue => [
          issue.asset_serial || 'N/A',
          issue.asset_id?.toString() || 'N/A',
          issue.issue_type,
          issue.due_date || 'N/A',
          issue.days_overdue.toString()
        ])
      ].map(row => row.join(',')).join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `compliance-report-${orgToday}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } finally {
      setIsExporting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <p className="text-red-600">Failed to load compliance data. Please try again later.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Compliance Dashboard</h1>
          <p className="text-gray-600 mt-1">Monitor expired certifications and overdue items</p>
        </div>
        <Button variant="outline" onClick={handleExport} disabled={isExporting}>
          {isExporting ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Download className="h-4 w-4 mr-2" />
          )}
          Export Report
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className={expiredCertifications.length > 0 ? 'border-red-200 bg-red-50' : ''}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Expired Certifications</CardTitle>
            <Award className={`h-4 w-4 ${expiredCertifications.length > 0 ? 'text-red-600' : 'text-gray-600'}`} />
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${expiredCertifications.length > 0 ? 'text-red-600' : ''}`}>
              {expiredCertifications.length}
            </div>
            <p className="text-xs text-gray-600 mt-1">Require immediate renewal</p>
          </CardContent>
        </Card>

        <Card className={overdueInspections.length > 0 ? 'border-orange-200 bg-orange-50' : ''}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Overdue Inspections</CardTitle>
            <AlertTriangle className={`h-4 w-4 ${overdueInspections.length > 0 ? 'text-orange-600' : 'text-gray-600'}`} />
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${overdueInspections.length > 0 ? 'text-orange-600' : ''}`}>
              {overdueInspections.length}
            </div>
            <p className="text-xs text-gray-600 mt-1">Need inspection now</p>
          </CardContent>
        </Card>

        <Card className={overdueMaintenance.length > 0 ? 'border-yellow-200 bg-yellow-50' : ''}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Overdue Maintenance</CardTitle>
            <Wrench className={`h-4 w-4 ${overdueMaintenance.length > 0 ? 'text-yellow-600' : 'text-gray-600'}`} />
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${overdueMaintenance.length > 0 ? 'text-yellow-600' : ''}`}>
              {overdueMaintenance.length}
            </div>
            <p className="text-xs text-gray-600 mt-1">Need servicing</p>
          </CardContent>
        </Card>
      </div>

      {/* Desktop Three-Column Layout */}
      <div className="hidden lg:grid lg:grid-cols-3 gap-6">
        {/* Expired Certifications */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Award className="h-5 w-5 text-red-600" />
              Expired Certifications
            </CardTitle>
            <CardDescription>Sorted by days overdue</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {expiredCertifications.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-8">No expired certifications</p>
            ) : (
              expiredCertifications.map((issue, index) => (
                <ComplianceIssueRow
                  key={`certification-${issue.asset_id ?? 'unresolved'}-${issue.due_date}-${index}`}
                  issue={issue}
                  dateLabel="Expired"
                />
              ))
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-600" />
              Overdue Inspections
            </CardTitle>
            <CardDescription>Sorted by days overdue</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {overdueInspections.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-8">No overdue inspections</p>
            ) : (
              overdueInspections.map((issue, index) => (
                <ComplianceIssueRow
                  key={`inspection-${issue.asset_id ?? 'unresolved'}-${issue.due_date}-${index}`}
                  issue={issue}
                  dateLabel="Due"
                />
              ))
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Wrench className="h-5 w-5 text-yellow-600" />
              Overdue Maintenance
            </CardTitle>
            <CardDescription>Sorted by days overdue</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {overdueMaintenance.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-8">No overdue maintenance</p>
            ) : (
              overdueMaintenance.map((issue, index) => (
                <ComplianceIssueRow
                  key={`maintenance-${issue.asset_id ?? 'unresolved'}-${issue.due_date}-${index}`}
                  issue={issue}
                  dateLabel="Due"
                />
              ))
            )}
          </CardContent>
        </Card>
      </div>

      {/* Mobile Tabbed Layout */}
      <div className="lg:hidden">
        <Tabs defaultValue="certifications">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="certifications">
              <Award className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">Certifications</span>
              <span className="sm:hidden">Certs</span>
            </TabsTrigger>
            <TabsTrigger value="inspections">
              <AlertTriangle className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">Inspections</span>
              <span className="sm:hidden">Insp</span>
            </TabsTrigger>
            <TabsTrigger value="maintenance">
              <Wrench className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">Maintenance</span>
              <span className="sm:hidden">Maint</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="certifications" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Expired Certifications</CardTitle>
                <CardDescription>Sorted by days overdue</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {expiredCertifications.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-8">No expired certifications</p>
                ) : (
                  expiredCertifications.map((issue, index) => (
                    <ComplianceIssueRow
                      key={`certification-${issue.asset_id ?? 'unresolved'}-${issue.due_date}-${index}`}
                      issue={issue}
                      dateLabel="Expired"
                    />
                  ))
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="inspections" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Overdue Inspections</CardTitle>
                <CardDescription>Sorted by days overdue</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {overdueInspections.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-8">No overdue inspections</p>
                ) : (
                  overdueInspections.map((issue, index) => (
                    <ComplianceIssueRow
                      key={`inspection-${issue.asset_id ?? 'unresolved'}-${issue.due_date}-${index}`}
                      issue={issue}
                      dateLabel="Due"
                    />
                  ))
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="maintenance" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Overdue Maintenance</CardTitle>
                <CardDescription>Sorted by days overdue</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {overdueMaintenance.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-8">No overdue maintenance</p>
                ) : (
                  overdueMaintenance.map((issue, index) => (
                    <ComplianceIssueRow
                      key={`maintenance-${issue.asset_id ?? 'unresolved'}-${issue.due_date}-${index}`}
                      issue={issue}
                      dateLabel="Due"
                    />
                  ))
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
